// P4 AI Tutor WP-03-CHAT-CORRECTION-R3 (doc 11) -- the single Markdown + math
// renderer for tutor replies. It replaces the earlier hand-written parser and
// matches the observable semantics of the reference console
// (react-markdown + remark-gfm + remark-math + rehype-katex): CommonMark + GFM
// (tables, strikethrough), `$…$` / `$$…$$` math, and no raw HTML.
//
// Stack (see doc 11 for why this and not `@mdit/plugin-katex`, which is
// ESM-only / Node >= 22 and cannot be built by this repo's webpack 4):
//   markdown-it 12  -- CommonMark/GFM parser (CJS, builds under webpack 4)
//   markdown-it-texmath -- math rules for `$…$`, `$$…$$`, `\(…\)`, `\[…\]`
//   katex           -- typesetting
//
// Security posture (the output of `renderMarkdown` is the ONLY thing ever given
// to `v-html`, see MarkdownContent.vue):
//   * `html: false`  -- raw HTML in a reply is escaped, so it shows as text;
//   * links are limited to http / https / mailto (everything else -- including
//     `javascript:`, `data:`, `vbscript:`, `file:` and relative / protocol-
//     relative URLs -- is not turned into a link at all) and always open with
//     `rel="noopener noreferrer nofollow"`;
//   * images are not parsed (no model-controlled request to a third party);
//   * KaTeX runs with `trust: false` (no `\href`, `\url`, `\includegraphics`,
//     `\htmlClass` ...), a size cap, `throwOnError: false` (a bad formula
//     shows as red source, never breaks the message);
//   * `linkify` stays OFF: bare-URL auto-linking goes through `linkify-it`,
//     which has known quadratic-time DoS advisories (fixed only in 6.x). Explicit
//     `[text](url)` and `<https://…>` links are unaffected. `typographer` stays
//     off too (markdown-it smartquotes advisory).
//
// CHAT-UX-01 (doc 29 §4.2): the same renderer, with the same posture, also
// renders a STUDENT's own message. One option exists for it,
// `literalIntrawordAsterisk`: a student writes multiplication with `*`, and
// plain emphasis rules would turn `2*3*4` into «2» + italic «3» + «4» --
// changing what the student wrote. With the option, a run of `*` standing
// directly between two operands is written as it is; every other emphasis
// works as before. Without the option (every tutor reply) nothing changes.
//
// CHAT-UX-01 delta (doc 29 §17.3 D-CX-7, §17.8): a second student-only option,
// `implicitMath`. A student writes `x^2 + 5y = z چیه` with no `$…$` at all;
// with the option, the mathematical runs of the plain text are found by
// implicitMath.js (conservatively: a strong signal, no prose, no unit, no
// URL ...) and typeset for DISPLAY, through the same KaTeX and the same
// options as every delimited formula; a run KaTeX refuses stays as typed.
// Only `text` tokens outside links are looked at, so code, delimited math and
// Markdown syntax are never touched, and the student's text itself is never
// changed. `renderMarkdownInline` renders ONE LINE of Markdown + math (a
// follow-up suggestion's label, D-CX-9): no block syntax exists for it, a
// link is only its text, and `$$…$$` is set inline.
//
// AT-API-020-H3 (doc 34/03 §7): a tutor-only option, `citationMarkers`. A
// Tutor API 0.2.0 reply cites its sources with numbered markers, `[^1]`,
// that point at the entries of its `citations` list (migration reference
// §4). With the option -- every tutor reply, never a student's text and
// never a suggestion label -- a marker is drawn as a small raised number
// (`<sup class="ai-md__cite">`), and `citationTitles` (number -> the source
// line) gives the ones the reply lists a `title`. Nothing else of the reply
// changes; without the option the renderer is what it was.
//
// POST-UAT-UX / PUX-A (doc 35/01 §1): `citationActions` (number -> the
// accessible name of "open this source's page") makes the markers of the
// sources that have a page image a button inside the superscript. Only the
// number and the escaped name are written -- never a URL; the click is
// handled by the component (MarkdownContent). Every other marker is the H3
// output byte for byte.
import MarkdownIt from 'markdown-it'
import texmath from 'markdown-it-texmath'
import katex from 'katex'
import { findImplicitMath } from './implicitMath'

const SAFE_LINK = /^(?:https?:|mailto:)/i

/**
 * @param {string} url
 * @returns {boolean} true only for an http, https or mailto URL
 */
export function isSafeLinkUrl(url) {
  return SAFE_LINK.test(String(url).trim())
}

const KATEX_OPTIONS = {
  throwOnError: false,
  // Persian text inside a formula (`\text{سرعت}`) is legitimate; do not log a
  // warning for every non-Latin glyph.
  strict: 'ignore',
  trust: false,
  maxSize: 50,
  maxExpand: 1000,
  output: 'htmlAndMathml',
}

// A streamed reply is re-rendered for every chunk, and the same formulas are
// typeset again each time -- remember the (deterministic) result per formula.
const KATEX_CACHE_LIMIT = 500
const katexCache = new Map()

const md = new MarkdownIt({
  html: false,
  linkify: false,
  typographer: false,
  // "Normal line breaks": a single newline in a reply is a line break.
  breaks: true,
})

const escapeHtml = md.utils.escapeHtml

function renderTex(tex, displayMode) {
  const key = `${displayMode ? 'D' : 'I'}:${tex}`
  const cached = katexCache.get(key)
  if (cached !== undefined) return cached
  let html
  try {
    html = katex.renderToString(tex, { ...KATEX_OPTIONS, displayMode })
  } catch (err) {
    html = `<span class="ai-md__math-error">${escapeHtml(tex)}</span>`
  }
  if (katexCache.size >= KATEX_CACHE_LIMIT) katexCache.clear()
  katexCache.set(key, html)
  return html
}

// `$…$` and `\(…\)` (inline), `$$…$$` and `\[…\]` (display). Both delimiter
// families are enabled; `\$` stays a literal dollar and a lone/currency `$ … $`
// is not math (texmath's own pandoc-style rule).
md.use(texmath, { engine: katex, delimiters: ['dollars', 'brackets'] })

// texmath renders through its own templates (custom <eq>/<eqn> elements); take
// over the render step so the math is wrapped in plain, styleable elements
// that are always left-to-right (a formula must not be re-ordered by the
// surrounding Persian text) and go through the KaTeX cache and options above.
const rules = md.renderer.rules
rules.math_inline = (tokens, idx) =>
  `<span class="ai-md__math" dir="ltr">${renderTex(
    tokens[idx].content,
    false
  )}</span>`
rules.math_inline_double = (tokens, idx) =>
  `<span class="ai-md__math-block" dir="ltr">${renderTex(
    tokens[idx].content,
    true
  )}</span>`
rules.math_block = (tokens, idx) =>
  `<div class="ai-md__math-block" dir="ltr">${renderTex(
    tokens[idx].content,
    true
  )}</div>\n`
rules.math_block_eqno = (tokens, idx) =>
  `<div class="ai-md__math-block" dir="ltr">${renderTex(
    tokens[idx].content,
    true
  )}<span class="ai-md__math-eqno">(${escapeHtml(
    tokens[idx].info
  )})</span></div>\n`

// ---- links: http / https / mailto only, always new-tab-safe
md.validateLink = isSafeLinkUrl
rules.link_open = (tokens, idx, options, env, self) => {
  tokens[idx].attrSet('target', '_blank')
  tokens[idx].attrSet('rel', 'noopener noreferrer nofollow')
  return self.renderToken(tokens, idx, options)
}

// ---- no images (`![alt](url)` degrades to a link, never an <img>)
md.disable('image')

// ---- layout hooks for the bubble: code and tables scroll on their own
const renderFence = rules.fence
rules.fence = (...args) =>
  renderFence(...args).replace('<pre>', '<pre class="ai-md__pre" dir="ltr">')
const renderCodeBlock = rules.code_block
rules.code_block = (...args) =>
  renderCodeBlock(...args).replace(
    '<pre>',
    '<pre class="ai-md__pre" dir="ltr">'
  )
rules.table_open = () => '<div class="ai-md__table"><table>\n'
rules.table_close = () => '</table></div>\n'

// ---- CHAT-UX-01: `*` between two operands is multiplication, not emphasis
// (only with `literalIntrawordAsterisk`, i.e. only for a student's message).
// An operand ends in a letter, a digit or a closing bracket and starts with a
// letter, a digit or an opening bracket: `2*3*4`, `a*b*c`, `2**3`,
// `(a+b)*(c+d)*(e+f)`. A `*` next to a space, a line edge or other
// punctuation is left to the ordinary emphasis rule («این *مهم* است»,
// «**نکته**.»).
const ASTERISK = 0x2a
const CLOSING_BRACKETS = ')]}'
const OPENING_BRACKETS = '([{'
const { isWhiteSpace, isPunctChar, isMdAsciiPunct } = md.utils

function isOperandChar(src, index, brackets) {
  const char = src.charAt(index)
  if (!char) return false
  if (brackets.indexOf(char) !== -1) return true
  const code = src.charCodeAt(index)
  return !isWhiteSpace(code) && !isMdAsciiPunct(code) && !isPunctChar(char)
}

md.inline.ruler.before(
  'emphasis',
  'literal_intraword_asterisk',
  (state, silent) => {
    if (!state.env || !state.env.literalIntrawordAsterisk) return false
    const start = state.pos
    if (state.src.charCodeAt(start) !== ASTERISK || start === 0) return false
    let end = start
    while (end < state.posMax && state.src.charCodeAt(end) === ASTERISK) {
      end += 1
    }
    if (end >= state.posMax) return false
    if (!isOperandChar(state.src, start - 1, CLOSING_BRACKETS)) return false
    if (!isOperandChar(state.src, end, OPENING_BRACKETS)) return false
    if (!silent) state.pending += state.src.slice(start, end)
    state.pos = end
    return true
  }
)

// ---- AT-API-020-H3: the citation markers of a tutor reply (API 0.2.0,
// migration reference §4; doc 34/03 §7). `[^n]` -- `n` one or more ASCII
// digits, at least 1, no spaces -- is the reply's pointer at the n-th entry
// of its `citations`; it becomes a small raised number,
// `<sup class="ai-md__cite" data-cite="n">n</sup>`. Only with
// `citationMarkers` (every tutor reply; never a student's own text, never a
// suggestion label), and only where ordinary text is parsed: inside code,
// math or an autolink the earlier rules have already consumed the
// characters. `citationTitles[n]`, when the turn lists that source, is the
// source line and becomes the superscript's `title`; a marker with no entry
// is still a superscript (the tutor's text is not rewritten) and never gains
// an entry. `[^a]`, `[^]`, `[^ 1]`, `[^0]`, `[^01]` and an unclosed `[^1`
// are not markers and stay literal.
//
// A `[^n]:` line (0.1-era footnote definition syntax, never written by
// 0.2.0) is dropped as a block of its own before the reference rule can
// read it -- it could otherwise turn every marker into a link.
const CITE_MARKER = /^\[\^([1-9][0-9]*)\]/
const CITE_DEFINITION = /^\[\^[0-9]+\]:/
// a marker is at most `[^` + 12 digits + `]`
const CITE_LOOKAHEAD = 16

md.inline.ruler.before('link', 'citation_marker', (state, silent) => {
  if (!state.env || !state.env.citationMarkers) return false
  if (state.src.charCodeAt(state.pos) !== 0x5b /* [ */) return false
  const match = CITE_MARKER.exec(
    state.src.slice(
      state.pos,
      Math.min(state.posMax, state.pos + CITE_LOOKAHEAD)
    )
  )
  if (!match) return false
  if (!silent) {
    const token = state.push('citation_marker', 'sup', 0)
    token.content = match[1]
  }
  state.pos += match[0].length
  return true
})

rules.citation_marker = (tokens, idx, options, env) => {
  const n = tokens[idx].content
  const titles = env && env.citationTitles
  const title =
    titles && typeof titles[n] === 'string' && titles[n]
      ? ` title="${escapeHtml(titles[n])}"`
      : ''
  const actions = env && env.citationActions
  if (actions && typeof actions[n] === 'string' && actions[n]) {
    return (
      `<sup class="ai-md__cite ai-md__cite--open" data-cite="${n}"${title}>` +
      `<button type="button" class="ai-md__cite-open" data-cite-open="${n}"` +
      ` aria-label="${escapeHtml(actions[n])}">${n}</button></sup>`
    )
  }
  return `<sup class="ai-md__cite" data-cite="${n}"${title}>${n}</sup>`
}

md.block.ruler.before(
  'reference',
  'citation_definition',
  (state, startLine, endLine, silent) => {
    if (!state.env || !state.env.citationMarkers) return false
    if (state.sCount[startLine] - state.blkIndent >= 4) return false
    const pos = state.bMarks[startLine] + state.tShift[startLine]
    const max = state.eMarks[startLine]
    if (
      !CITE_DEFINITION.test(
        state.src.slice(pos, Math.min(max, pos + CITE_LOOKAHEAD))
      )
    ) {
      return false
    }
    if (silent) return true
    state.line = startLine + 1
    return true
  },
  { alt: ['paragraph', 'reference', 'blockquote', 'list'] }
)

// ---- CHAT-UX-01 delta: undelimited math in a student's text (display only,
// only with `implicitMath`). Runs after the inline parser, on `text` tokens
// outside links: each run implicitMath.js finds and KaTeX accepts becomes a
// `math_inline` token (rendered exactly like a `$…$` formula); the text
// around it stays a text token. A run KaTeX refuses is left in the text.
const IMPLICIT_CACHE_LIMIT = 500
const implicitCache = new Map()

function katexAccepts(tex) {
  const cached = implicitCache.get(tex)
  if (cached !== undefined) return cached
  let ok
  try {
    katex.renderToString(tex, {
      ...KATEX_OPTIONS,
      throwOnError: true,
      displayMode: false,
    })
    ok = true
  } catch (err) {
    ok = false
  }
  if (implicitCache.size >= IMPLICIT_CACHE_LIMIT) implicitCache.clear()
  implicitCache.set(tex, ok)
  return ok
}

function splitImplicitMath(token, Token) {
  const runs = findImplicitMath(token.content).filter(run =>
    katexAccepts(run.tex)
  )
  if (!runs.length) return [token]
  const out = []
  let cursor = 0
  const pushText = text => {
    if (!text) return
    const t = new Token('text', '', 0)
    t.content = text
    out.push(t)
  }
  runs.forEach(run => {
    pushText(token.content.slice(cursor, run.start))
    const m = new Token('math_inline', 'math', 0)
    m.content = run.tex
    m.markup = ''
    m.meta = { implicit: true, source: run.source }
    out.push(m)
    cursor = run.end
  })
  pushText(token.content.slice(cursor))
  return out
}

md.core.ruler.after('inline', 'implicit_math', state => {
  if (!state.env || !state.env.implicitMath) return
  state.tokens.forEach(block => {
    if (block.type !== 'inline' || !block.children) return
    let linkDepth = 0
    const children = []
    block.children.forEach(child => {
      if (child.type === 'link_open') linkDepth += 1
      else if (child.type === 'link_close') linkDepth -= 1
      if (child.type === 'text' && linkDepth === 0) {
        children.push(...splitImplicitMath(child, state.Token))
      } else {
        children.push(child)
      }
    })
    block.children = children
  })
})

function envFor(options) {
  const titles = options && options.citationTitles
  const actions = options && options.citationActions
  return {
    literalIntrawordAsterisk: !!(options && options.literalIntrawordAsterisk),
    implicitMath: !!(options && options.implicitMath),
    citationMarkers: !!(options && options.citationMarkers),
    citationTitles: titles && typeof titles === 'object' ? titles : null,
    citationActions: actions && typeof actions === 'object' ? actions : null,
  }
}

/**
 * Renders a tutor reply -- or, with `options`, a student's own message --
 * (Markdown + LaTeX) to an HTML string. Total: partial input from an
 * in-progress stream (an unclosed fence, `$`, `$$`, table row ...) never
 * throws -- what is not yet complete simply stays as source text.
 * @param {string} source
 * @param {{literalIntrawordAsterisk?: boolean, implicitMath?: boolean,
 *   citationMarkers?: boolean, citationTitles?: Object,
 *   citationActions?: Object}}
 *   [options] see the notes at the top of this file: a tutor reply passes
 *   `citationMarkers` (and `citationTitles`, its sources' lines by number),
 *   a student's message the two student options
 * @returns {string} HTML produced only by markdown-it / KaTeX (raw HTML in
 *   `source` is escaped)
 */
export function renderMarkdown(source, options) {
  if (typeof source !== 'string' || source === '') return ''
  try {
    return md.render(source, envFor(options))
  } catch (err) {
    // Defensive: never let one malformed reply break the chat.
    return `<p>${escapeHtml(source)}</p>`
  }
}

/**
 * Renders ONE LINE of Markdown + LaTeX (a follow-up suggestion's label,
 * doc 29 §17.3 D-CX-9) to an HTML string with no block element in it: the
 * inline parser only, so a heading mark, a list mark, a fence or a `figure`
 * block cannot exist; a link is reduced to its text (nothing clickable may
 * sit inside the card's button); `$$…$$` is set inline; a line break is a
 * space. Same posture as `renderMarkdown`: raw HTML escaped, KaTeX with
 * `trust: false`.
 * @param {string} source
 * @param {{literalIntrawordAsterisk?: boolean, implicitMath?: boolean}}
 *   [options]
 * @returns {string}
 */
export function renderMarkdownInline(source, options) {
  if (typeof source !== 'string' || source === '') return ''
  try {
    const env = envFor(options)
    const tokens = md.parseInline(source, env)
    tokens.forEach(block => {
      if (!block.children) return
      block.children = block.children
        .filter(t => t.type !== 'link_open' && t.type !== 'link_close')
        .map(t => {
          if (t.type === 'math_inline_double') t.type = 'math_inline'
          if (t.type === 'softbreak' || t.type === 'hardbreak') {
            t.type = 'text'
            t.content = ' '
          }
          return t
        })
    })
    return md.renderer.render(tokens, md.options, env)
  } catch (err) {
    return escapeHtml(source)
  }
}

export default renderMarkdown
